import { makeStyles } from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Client } from "@stomp/stompjs";
import jwt_decode from "jwt-decode";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import SockJS from "sockjs-client";
import ProfileContext from "../../components/ProfileContext";
import {
  clearNotifications,
  getVisitorEvents,
} from "../../slices/VisitosEvent/VisitorsEventSlice";
import { ThemeContext } from "../../theme/ThemeContext";
import PushNotification from "../PushNotification/PushNotification";
import CustomStyles from "./CustomStyles";
import FrontDeskTeamsAlert from "./FrontDeskTeamsAlert";

// One notification per visit + status (the same event can arrive more than once)
const eventKey = (item) =>
  item?.visitId ? `${item.visitId}-${item.status}` : item?.id;

const uniqueEvents = (list) => {
  const seen = new Set();
  return list.filter((item) => {
    const key = eventKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getRoleCode = () => {
  try {
    const token = localStorage.getItem(process.env.REACT_APP_ACCESS_TOKEN);
    return token ? jwt_decode(token)?.roleCode : "";
  } catch (e) {
    return "";
  }
};

const useStyles = makeStyles((theme) => ({
  dialogbox: {
    "& .MuiDialog-paper": {
      minWidth: "20%",
      marginLeft: "80%",
      marginTop: "75px",
      height: "-webkit-fill-available",
      paddingBotton: "0px",
    },
  },
  input: {
    "&:-webkit-autofill": {
      WebkitTextFillColor: "#000 !important",
    },
  },
  dialogPaper: {
    borderRadius: "20px",
  },
}));

export default function VisitorsExpiredEvents() {
  const [open, setOpen] = useState(false);
  const { darkMode } = useContext(ThemeContext);
  const [hovered, setHovered] = useState(false);
  const classes = useStyles({ darkMode });
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // const messages = useSelector((state) => state.notifications.messages);
  const [eventData, setEventData] = useState([]);
  const { visitorEvents, setVisitorsNotCheckedOut } = useContext(
    ProfileContext,
  );
  const [notification, setNotification] = useState(false);
  const [frontDeskAlert, setFrontDeskAlert] = useState(false);
  const isFrontDesk = getRoleCode() === "FRONTDESK";

  const dummyData = [
    {
      id: 1,
      visitorName: "Somashekar",
      status: "SCHEDULED",
    },
    {
      id: 2,
      visitorName: "Somashekar",
      status: "CONFIRMED",
    },
    {
      id: 3,
      visitorName: "Somashekar",
      status: "CANCELLED",
    },
  ];

  useEffect(() => {
    // 1. Connect to WebSocket endpoint
    const socket = new SockJS(`${window.location.origin}/ws`); // Replace with your backend URL
    const stompClient = new Client({
      webSocketFactory: () => socket,
      debug: (str) => console.log("[STOMP]", str),
    });

    // 2. On successful connection, subscribe to topic
    stompClient.onConnect = () => {
      console.log("Connected to WebSocket");
      stompClient.subscribe(
        "/visitor/status/" + localStorage.getItem("companyId"),
        (message) => {
          console.log("Expired Event Received", message.body);
          const newData = JSON.parse(message.body);
          console.log(newData);
          handleNewData(newData);
        },
      );
      stompClient.subscribe(
        "/visitor/status/update/" + localStorage.getItem("companyId"),
        (message) => {
          let data = JSON.parse(message.body);
          setEventData((prev) => {
            const updatedEvents = prev.filter(
              (item) => item.id != data.visitorId,
            );
            setVisitorsNotCheckedOut(updatedEvents.length);
            return updatedEvents;
          });
        },
      );
    };
    stompClient.activate();

    // Cleanup on unmount
    return () => {
      stompClient.deactivate();
      console.log("WebSocket disconnected");
    };
  }, [localStorage.getItem("companyId")]);

  useEffect(() => {
    dispatch(getVisitorEvents())
      .unwrap()
      .then((res) => {
        if (res.code === "CVAI0000") {
          setEventData(uniqueEvents(Array.isArray(res.data) ? res.data : []));
          console.log(res.data);
        }
      });
  }, []);

  const formatStatus = (value) => {
    if (!value) return "";

    if (value.toUpperCase() === "EXPIRED") {
      return "Time Expired";
    }

    return value
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleNewData = (data) => {
    setEventData((prev) => {
      let items = [];

      if (!data) return prev;

      const incoming = Array.isArray(data) ? data : [data];
      const seen = new Set(prev.map(eventKey));
      items = incoming.filter((item) => {
        if (!item?.id || seen.has(eventKey(item))) return false;
        seen.add(eventKey(item));
        return true;
      });

      if (items.length === 0) return prev;

      // Show push notification only when notification dialog is closed
      if (!open) {
        const latest = items[0];

        // Front Desk gets the dedicated alert for new pre-registrations
        if (isFrontDesk && latest.status === "PENDING_ARRIVAL") {
          setFrontDeskAlert({
            open: true,
            visitorName: latest.visitorName,
          });
        } else {
          setNotification({
            open: true,
            message: `${latest.visitorName} - ${formatStatus(latest.status)}`,
          });
        }
      }

      return [...items, ...prev];
    });
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClearNotifications = () => {
    dispatch(clearNotifications())
      .unwrap()
      .then((res) => {
        setEventData([]);
      });
  };

  const getVisitorDescription = (status) => {
    switch (status) {
      case "SCHEDULED":
      case "CONFIRMED":
      case "REJECTED":
      case "APPROVED":
      case "CANCELLED":
      case "RESCHEDULED":
      case "COMPLETED":
      case "LOGGED":
      case "PENDING_ARRIVAL":
        return "Appointment has been";
      case "CHECKED_IN":
      case "CHECKED_OUT":
      case "INITIATED_FOR_BLOCKED":
      case "BLOCKED":
        return "Has been ";
      case "EXPIRED":
        return "Appointment";
      case "MISSED":
        return "Appointment is";
      default:
        return "Has been";
    }
  };

  return (
    <>
      <IconButton
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => setOpen(!open)}
        style={{
          color: darkMode ? "#fff" : "#474a4e",
          opacity: 0.8,
          marginRight: "4px",
        }}
      >
        {hovered ? (
          <Badge
            variant="dot"
            invisible={!eventData.length > 0}
            overlap="circular"
            sx={{
              "& .MuiBadge-dot": {
                backgroundColor: darkMode ? "#fff" : "#474a4e",
              },
            }}
          >
            <NotificationsIcon fontSize="large" />
          </Badge>
        ) : (
          <Badge
            variant="dot"
            invisible={!eventData.length > 0}
            overlap="circular"
            sx={{
              "& .MuiBadge-dot": {
                backgroundColor: darkMode ? "#fff" : "#474a4e",
              },
            }}
          >
            <NotificationsNoneIcon fontSize="large" />
          </Badge>
        )}
      </IconButton>

      <Dialog
        open={open}
        onClose={handleClose}
        className={classes.dialogbox}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle
          id="form-dialog-title"
          variant="h4"
          style={{
            backgroundColor: darkMode ? "#0E2A41" : "#fff",
            position: "sticky",
            top: 0,
            zIndex: 10,
            color: darkMode ? "#fff" : "#000",
            fontSize: "21px",
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 15,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          {t("NOTIFICATIONS")}
        </DialogTitle>
        <Divider
          variant="middle"
          style={{
            background: darkMode
              ? "transparent linear-gradient(90deg, rgba(190, 231, 255, 0.05) 0%, rgba(34, 107, 171, 0.5) 47%, rgba(190, 231, 255, 0.05) 100%) 0% 0% no-repeat padding-box"
              : "transparent linear-gradient(90deg, rgba(190, 231, 255, 0.05) 0%, rgba(34, 107, 171, 0.5) 47%, rgba(190, 231, 255, 0.05) 100%) 0% 0% no-repeat padding-box",
            height: "1px",
            opacity: 0.5,
          }}
        />
        <DialogContent
          style={{
            backgroundColor: darkMode ? "#0E2A41" : "#fff",
            paddingTop: "4px",
          }}
        >
          <Grid
            xs={12}
            style={{
              display: "flex",
              justifyContent: "end",
            }}
          >
            <Button
              onClick={handleClearNotifications}
              sx={{
                height: "16px",
                fontSize: "14px",
                "&:hover": {
                  backgroundColor: darkMode ? "#ffffff14" : "rgb(0 0 0 / 4%)",
                },
              }}
              // variant="text"
            >
              Clear All
            </Button>
          </Grid>

          <Stack
            spacing={1}
            style={{
              paddingTop: "4px",
              overflowY: "auto",
              scrollbarWidth: "thin",
            }}
          >
            {eventData.map((item) => (
              <>
                <Box
                  style={{
                    backgroundColor: darkMode
                      ? "#021c31"
                      : "rgb(61 73 119 / 6%)",
                    borderRadius: "8px",
                  }}
                >
                  <Typography
                    key={item.id}
                    variant="body1"
                    style={{
                      fontSize: "18px",
                      fontWeight: "bold",
                      color: darkMode ? "white" : "black",
                      display: "flex",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                      display: "block",
                    }}
                  >
                    {item.visitorName}
                  </Typography>

                  <Typography
                    key={item.id}
                    variant="body1"
                    style={{
                      fontSize: "12px",
                      color: darkMode ? "white" : "black",
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: "10px",
                      gap: "6px",
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    {getVisitorDescription(item.status)}

                    <CustomStyles row={item.status} icon={false} />
                  </Typography>
                </Box>
              </>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>

      <FrontDeskTeamsAlert
        open={frontDeskAlert.open}
        visitorName={frontDeskAlert.visitorName}
        onClose={() =>
          setFrontDeskAlert((prev) => ({
            ...prev,
            open: false,
          }))
        }
      />

      <PushNotification
        open={notification.open}
        message={notification.message}
        onClose={() =>
          setNotification((prev) => ({
            ...prev,
            open: false,
          }))
        }
      />
    </>
  );
}
